/**
 * 全編モーション (映像スタイル「モーショングラフィックス」) のシナリオ生成ボタン直後のモーダル
 * (docs/features/brand-cut-motion-graphics-plan.md §10.5「BGMを先に決める」)。
 *
 * BGM の決め方は 5 択: 「生成する / 素材から選ぶ / フリー楽曲 / アップロードする / 後で付ける・なし」。
 * 生成は作曲 AI の選択と「BGMができたら続けてシナリオを生成する」(既定オン・どちらも最後の選択を localStorage に保存)。
 * 曲の指定はシナリオ編集画面の BGM セクションに揃える (2026-10-02): プロンプトの方式 (標準 / テンプレート)・ボーカル・
 * 曲の詳細 (ジャンル / ムード / BPM / キー)。プロンプト本文の編集は無い (拍の解析用の指示を含むモーション専用プロンプトは
 * backend が組む・非開示)。開くたびに既定 (標準・ボーカルなし・詳細なし) に戻す。料金は生成ボタンのバッジに合計
 * (台本 + 生成なら BGM) を出し、フッターには生成タブのときだけ「うちBGM生成 n ポイント」の内訳を出す
 * (各シーンのモーションの定額と「1 案だけ」の注記は 2026-10-02 ユーザー指示で削除)。
 * BGM があるとき (生成以外のBGMあり) は「BGMの拍に合わせる」(既定オン・§10.9) をここで決める: 台本を小節で書くか
 * 秒で書くかが変わるので、生成後のカードでは変えられない (作り直し)。
 *
 * 素材から選ぶ / フリー楽曲 / アップロード はいずれも「組織の既存音声素材をBGMに指定する」ので、API 上は
 * 同じ `mode: 'upload'` (material_id) で送る (フリー楽曲は確定時に copy-to-materials で組織素材へ複製。複製は冪等)。
 *
 * レイアウトは BrandCutModal と同じ全画面カード (modal-overlay + modal-card)。決め方は上部の横並びタブ
 * (動画プロンプト入力モードの「AIおまかせ / 静止画フレーム」と同じ modeTabButton スタイル) で、選んだ決め方の説明と
 * 設定をその下に出す (2026-10-02)。
 */
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useToast } from '../../../hooks/useToast';
import { GenerateButton } from '../common/GenerateButton';
import { SegmentedTabs } from '../../common/SegmentedTabs';
import { SingleSelectDropdown } from '../../common/SingleSelectDropdown';
import { ProviderSegmentedControl } from './ProviderSegmentedControl';
import { BgmSongDetailFields, type BgmSongDetailValue } from './BgmSongDetailFields';
import { useScenarioAttributesStateSafe } from '../state/scenarioAttributesContext';
import { MaterialsPickerModal } from '../../materials/MaterialsPickerModal';
import { formatAudioDuration } from '../../common/AudioPlayButton';
import { BgmFreeTrackPickerModal } from './BgmFreeTrackPicker';
import { BgmUploadDropZone } from './BgmUploadDropZone';
import { BgmOrangePlayButton } from './BgmOrangePlayButton';
import { resolveBgmDisplayName, type BgmDisplayNameSource } from './bgmDisplayName';
import { fetchMaterialById, fetchMaterialSignedUrl } from '../../../features/materials/api';
import { copyAudioCatalogItemToMaterials, type AudioCatalogItem } from '../../../features/audioCatalog/api';
import type { BgmProvider, MotionBgmChoice, MotionBgmSong } from '../../../features/scenarios/api';
import { BGM_UPLOAD_ACCEPT, uploadBgmFile } from './bgmUpload';
import {
  combineMotionPrepareCredits,
  formatCreditRange,
  motionBgmCredits,
  motionBgmDurationSeconds,
  type CreditRange,
} from './motionScenario';
import { ListMusic, Music4, type LucideIcon } from 'lucide-react';
import styles from './MotionBgmChoiceModal.module.css';
import pickerStyles from './BgmFreeTrackPicker.module.css';
import sectionStyles from './ScenarioGenerator.module.css';
import { AUDIO_PURPOSE_SOURCE_FILTER } from '../../../features/materials/sourceLabels';

/** モーダル内の決め方 (API の MotionBgmChoice.mode とは別。material / free は 'upload' に写す) */
type Mode = 'generate' | 'material' | 'free' | 'upload' | 'none';

const BGM_PROVIDER_KEY = 'bgm_provider_last';

/** BgmSection と同じ既定 (最後に選んだ作曲 AI・無ければ ElevenLabs) */
function readStoredBgmProvider(): BgmProvider {
  try {
    const v = localStorage.getItem(BGM_PROVIDER_KEY);
    if (v === 'elevenlabs' || v === 'lyria-pro') return v;
    // Gemini のクリップ (lyria) は尺を指定できず常に約 30 秒になるため選択肢から外した (2026-09-29)。保存値はフル曲へ寄せる
    if (v === 'lyria') return 'lyria-pro';
  } catch { /* ignore */ }
  return 'elevenlabs';
}

const BGM_AUTO_CONTINUE_KEY = 'motion_bgm_auto_continue_last';

/** 「BGMの生成が完了したら、試聴せずにシナリオ生成まで行う」の最後の選択 (無ければ既定オン) */
function readStoredAutoContinue(): boolean {
  try {
    const v = localStorage.getItem(BGM_AUTO_CONTINUE_KEY);
    if (v === 'false') return false;
  } catch { /* ignore */ }
  return true;
}

/** 決め方の 5 択 (名称・順序は 2026-10-02 ユーザー指定)。アイコンは SVG を mask で抜き、タブの文字色に追従させる
 *  (BGM セクションの入力方法タブと同じ素材・方式) */
// タブ先頭のアイコンは Streamline の SVG (iconMaskUrl・mask で塗る) か lucide のコンポーネント (Icon) のどちらか
const MODE_OPTIONS: ReadonlyArray<{ value: Mode; label: string; iconMaskUrl?: string; Icon?: LucideIcon; desc: string }> = [
  { value: 'generate', label: '生成', iconMaskUrl: '/scene_auto.svg', desc: 'AI がモーショングラフィックスに適したBGMを作曲します。' },
  { value: 'material', label: '素材', Icon: ListMusic, desc: 'すでに登録済みのBGM素材を利用します。' },
  { value: 'upload', label: 'アップロード', iconMaskUrl: '/audio_file_mp_3.svg', desc: 'お手持ちの音源を利用します。' },
  { value: 'free', label: 'フリー楽曲', iconMaskUrl: '/music_on_off_1.svg', desc: '著作権フリーの楽曲を利用します。' },
  { value: 'none', label: 'なし／後付け', iconMaskUrl: '/volume_control_mute_1.svg', desc: 'BGMを設定せずにシナリオを生成します。後からBGMを付けることも可能です。' },
];

/** 素材ピッカーの取得元の初期値 (用途が BGM のもの = 生成した BGM + 用途を BGM にしてアップロードした音声)。参照を安定させるためモジュールレベル */
const PICKER_INITIAL_SOURCE_TYPES = [AUDIO_PURPOSE_SOURCE_FILTER.bgm];
const PICKER_FILE_TYPES = ['audio'];

/** 作曲 AI。シナリオ編集画面のBGMセクション (作曲AIモデル タブ) と同じ部品・アイコン */
const PROVIDER_OPTIONS: ReadonlyArray<{ value: BgmProvider; label: string; iconMaskUrl: string; tooltip: string }> = [
  { value: 'elevenlabs', label: 'ElevenLabs', iconMaskUrl: '/logos/elevenlabs-symbol.svg', tooltip: '秒数を指定して生成' },
  { value: 'lyria-pro', label: 'Gemini', iconMaskUrl: '/logos/google_gemini_icon.svg', tooltip: '想定尺に合わせて 1 曲生成' },
];

/** プロンプトの方式。シナリオ編集画面の「標準 / テンプレート」と同じ (歌詞は全編モーションでは使えないので無い) */
type PromptMode = 'standard' | 'template';

/** BPM の範囲 (backend の音の構成案 section_plan.MIN_BPM / MAX_BPM と同じ) */
const MOTION_BGM_BPM_MIN = 60;
const MOTION_BGM_BPM_MAX = 180;

/** 選択中の既存素材 / アップロード済みの曲 (表示用)。url は試聴用の署名付き URL (取れなければ null) */
interface PickedMaterial { materialId: string; name: string; durationSeconds: number | null; url: string | null }

export interface MotionBgmChoiceModalProps {
  isOpen: boolean;
  /** シナリオ生成の料金 (ランダムは 1 モデル分のレンジ)。分からなければ null */
  scenarioCredits: CreditRange | null;
  /** 想定尺 (秒)。BGMの尺 (想定尺 + 余白) の料金見積りに使う */
  expectedSeconds: number | null;
  creditRates: ReadonlyArray<{ service_type: string; resource_type: string; credits_per_unit: number }> | undefined;
  onCancel: () => void;
  onConfirm: (choice: MotionBgmChoice) => void;
}

export function MotionBgmChoiceModal({
  isOpen, scenarioCredits, expectedSeconds, creditRates, onCancel, onConfirm,
}: MotionBgmChoiceModalProps) {
  const toast = useToast();
  const [mode, setMode] = useState<Mode>('generate');
  const [provider, setProvider] = useState<BgmProvider>(readStoredBgmProvider);
  const [autoContinue, setAutoContinue] = useState(readStoredAutoContinue);
  const [beatSync, setBeatSync] = useState(true);
  const [promptMode, setPromptMode] = useState<PromptMode>('standard');
  const [templateKey, setTemplateKey] = useState<string | null>(null);
  const [vocal, setVocal] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detail, setDetail] = useState<BgmSongDetailValue>({});
  /** BGM テンプレート (属性ツリー)。無ければ方式の切り替え自体を出さない (シナリオ編集画面と同じ) */
  const bgmTemplateOptions = useScenarioAttributesStateSafe()?.bgmTemplateOptions ?? [];
  const templateMode = promptMode === 'template' && bgmTemplateOptions.length > 0;
  const effectiveTemplateKey = bgmTemplateOptions.find((o) => o.value === templateKey)?.value ?? bgmTemplateOptions[0]?.value ?? null;
  /** アップロード中 / フリー楽曲の複製中 (確定ボタンを止める) */
  const [busy, setBusy] = useState(false);
  const [uploaded, setUploaded] = useState<PickedMaterial | null>(null);
  /** アップロード済みの曲があるときにドロップ領域を再表示するか (「別のファイルをアップロード」で true) */
  const [uploadZoneOpen, setUploadZoneOpen] = useState(false);
  const [pickedMaterial, setPickedMaterial] = useState<PickedMaterial | null>(null);
  const [pickedFreeTrack, setPickedFreeTrack] = useState<AudioCatalogItem | null>(null);
  const [materialPickerOpen, setMaterialPickerOpen] = useState(false);
  const [freePickerOpen, setFreePickerOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // 開くたびに初期値へ (選んだ曲は同じ操作の続きとして残さない)
  useEffect(() => {
    if (!isOpen) return;
    setMode('generate');
    setProvider(readStoredBgmProvider());
    setAutoContinue(readStoredAutoContinue());
    setBeatSync(true);
    setPromptMode('standard');
    setTemplateKey(null);
    setVocal(false);
    setDetailOpen(false);
    setDetail({});
    setUploaded(null);
    setUploadZoneOpen(false);
    setPickedMaterial(null);
    setPickedFreeTrack(null);
    setMaterialPickerOpen(false);
    setFreePickerOpen(false);
    setBusy(false);
  }, [isOpen]);

  const bgmSeconds = motionBgmDurationSeconds(expectedSeconds);
  const bgmCredits = motionBgmCredits(creditRates, provider, bgmSeconds);
  const credits = combineMotionPrepareCredits({ scenario: scenarioCredits, bgm: bgmCredits, generateBgm: mode === 'generate' });

  /** 素材の試聴 URL と尺 (best-effort)。一時フォルダ (temporary) → 共有フォルダ (cloud) の順に引く */
  const loadMaterialPlayback = async (materialId: string): Promise<{ url: string | null; durationSeconds: number | null }> => {
    let url: string | null = null;
    let durationSeconds: number | null = null;
    try {
      url = await fetchMaterialSignedUrl(materialId, true);
    } catch {
      try {
        url = await fetchMaterialSignedUrl(materialId, false);
      } catch { /* ignore */ }
    }
    try {
      const m = await fetchMaterialById(materialId);
      if (typeof m?.duration_seconds === 'number') durationSeconds = m.duration_seconds;
    } catch { /* ignore */ }
    return { url, durationSeconds };
  };

  const handleFile = async (file: File | undefined) => {
    if (!file) return;
    setBusy(true);
    try {
      const r = await uploadBgmFile(file);
      // 素材 / フリー楽曲タブと同じく、アップロード済みの曲も試聴できるようにする
      const { url, durationSeconds } = await loadMaterialPlayback(r.materialId);
      setUploaded({ materialId: r.materialId, name: r.name, durationSeconds, url });
      setUploadZoneOpen(false);
    } catch {
      toast.error('BGMのアップロードに失敗しました');
    } finally {
      setBusy(false);
    }
  };

  const handlePickMaterial = async (materialId: string) => {
    setMaterialPickerOpen(false);
    // 表示名は best-effort (取れなければ「BGM」)。BGMセクションのヘッダーと同じ規則
    // (name > song_title > 元ファイル名 > 「BGM (自動生成)」)
    let name = 'BGM';
    try {
      const m = await fetchMaterialById(materialId);
      name = resolveBgmDisplayName(m as BgmDisplayNameSource | null) || name;
    } catch { /* ignore */ }
    const { url, durationSeconds } = await loadMaterialPlayback(materialId);
    setPickedMaterial({ materialId, name, durationSeconds, url });
  };

  /** 曲の詳細の BPM が範囲外 (空は「指定なし」= トーンから決める) */
  const bpmInvalid = mode === 'generate' && !templateMode && detailOpen && detail.bpm != null
    && (!Number.isInteger(detail.bpm) || detail.bpm < MOTION_BGM_BPM_MIN || detail.bpm > MOTION_BGM_BPM_MAX);

  /** 確定できるか: 素材系は選択済みが必要。アップロード中 / 複製中は不可 */
  const missing: string | null =
    bpmInvalid ? `BPMは${MOTION_BGM_BPM_MIN}〜${MOTION_BGM_BPM_MAX}の整数で指定してください`
    : mode === 'upload' && !uploaded ? '音源ファイルを選択してください'
      : mode === 'material' && !pickedMaterial ? '音声素材を選択してください'
        : mode === 'free' && !pickedFreeTrack ? '楽曲を選択してください'
          : null;
  const canConfirm = !busy && missing == null;

  const handleConfirm = async () => {
    if (!canConfirm) return;
    if (mode === 'generate') {
      try {
        localStorage.setItem(BGM_PROVIDER_KEY, provider);
        localStorage.setItem(BGM_AUTO_CONTINUE_KEY, String(autoContinue));
      } catch { /* ignore */ }
      // 曲の指定: テンプレートはキーだけ。標準はボーカルと、「曲の詳細を設定する」のときだけ詳細 (空欄はトーンから決める)
      const song: MotionBgmSong = templateMode && effectiveTemplateKey
        ? { template_key: effectiveTemplateKey }
        : {
          vocal,
          ...(detailOpen ? {
            genre: detail.genre?.trim() ?? '',
            mood: detail.mood?.trim() ?? '',
            bpm: detail.bpm ?? null,
            musical_key: detail.musical_key?.trim() ?? '',
          } : {}),
        };
      onConfirm({ mode: 'generate', provider, auto_continue: autoContinue, beat_sync: beatSync, song });
    } else if (mode === 'upload' && uploaded) {
      onConfirm({ mode: 'upload', material_id: uploaded.materialId, beat_sync: beatSync });
    } else if (mode === 'material' && pickedMaterial) {
      onConfirm({ mode: 'upload', material_id: pickedMaterial.materialId, beat_sync: beatSync });
    } else if (mode === 'free' && pickedFreeTrack) {
      // BgmSection と同じく組織の素材へ複製してから紐付ける (カタログの提供終了で壊れないように)。複製は冪等
      setBusy(true);
      try {
        const copied = await copyAudioCatalogItemToMaterials(pickedFreeTrack.id);
        onConfirm({ mode: 'upload', material_id: copied.material_id, beat_sync: beatSync });
      } catch {
        toast.error('楽曲を素材に取り込めませんでした');
      } finally {
        setBusy(false);
      }
    } else {
      onConfirm({ mode: 'none' });
    }
  };

  if (!isOpen) return null;

  // フッターの内訳。ボタンのバッジが合計 (台本 + BGM) を出すので、ここは生成タブでBGM生成分の内訳だけを出す
  // (BGM のポイントは「シナリオを生成」を押した時点で引かれ、台本分はBGM完成後に引かれる。確認画面に料金表示は無い)。
  // 料金表が取れないときは「BGMの料金は別途」と注記する。他のタブは追加料金が無いので何も出さない
  const costLine: string | null =
    mode !== 'generate' ? null
      : bgmCredits != null ? `うちBGM生成 ${bgmCredits} ポイント`
        : credits?.bgmUnknown ? 'BGMの料金は別途' : null;

  /** 「選択中」行 (フリー楽曲タブのインライン部分と同じ見た目) */
  const renderCurrentRow = (picked: { name: string; durationSeconds: number | null; url: string | null } | null, emptyText: string, buttonLabel: [string, string], onOpen: () => void) => (
    <div className={pickerStyles.currentRow}>
      <span className={pickerStyles.currentLabel}>選択中</span>
      {picked ? (
        <>
          {/* BGM セクションの採用中BGMと同じオレンジの再生ボタン */}
          <BgmOrangePlayButton src={picked.url} />
          <span className={pickerStyles.currentName} title={picked.name}>
            {picked.name}
            {picked.durationSeconds ? `（${formatAudioDuration(picked.durationSeconds)}）` : ''}
          </span>
        </>
      ) : (
        <span className={pickerStyles.currentEmpty}>{emptyText}</span>
      )}
      <button
        type="button"
        className={sectionStyles.storyboardUploadTileButton}
        style={{ fontSize: '13px' }}
        disabled={busy}
        onClick={onOpen}
      >
        {picked ? buttonLabel[1] : buttonLabel[0]}
      </button>
    </div>
  );

  return createPortal(
    <>
      {/* 入力内容 (選んだ曲など) の消失を防ぐため、オーバーレイのクリックでは閉じない (✕ / キャンセルのみ)。
          ページから開くモーダルなので nested にしない (素材 / フリー楽曲のピッカーが上に重なれるように) */}
      <div className="modal-overlay">
        <div className={`modal-card ${styles.fullCard}`} role="dialog" aria-modal="true" aria-labelledby="motion-bgm-choice-title">
          <div className="modal-header">
            <h2 className={`modal-title ${styles.titleWithIcon}`} id="motion-bgm-choice-title">
              <Music4 size={18} className={styles.titleIcon} aria-hidden="true" />
              BGMの設定方法
            </h2>
            <button type="button" className="modal-close-button" onClick={onCancel} aria-label="閉じる" disabled={busy}>
              ×
            </button>
          </div>

          <div className={styles.body}>
            <p className={styles.intro}>
              モーショングラフィックスはBGMに合わせた台本を書きます
            </p>

            {/* 決め方の 5 択: 上部の横並びタブ。動画プロンプト入力モードの「AIおまかせ / 静止画フレーム」と同じスタイル */}
            <div className={styles.modeTabs}>
              <div className={sectionStyles.modeTabButtons} role="tablist" aria-label="BGMの決め方">
                {MODE_OPTIONS.map((opt) => {
                  const active = mode === opt.value;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      role="tab"
                      aria-selected={active}
                      className={`${sectionStyles.modeTabButton} ${active ? sectionStyles.modeTabButtonActive : ''}`}
                      onClick={() => { if (!active) setMode(opt.value); }}
                      disabled={busy}
                    >
                      {opt.Icon ? (
                        <opt.Icon size={18} className={styles.modeTabLucideIcon} aria-hidden="true" />
                      ) : (
                        <span
                          className={styles.modeTabIcon}
                          style={{ WebkitMaskImage: `url(${opt.iconMaskUrl})`, maskImage: `url(${opt.iconMaskUrl})` }}
                          aria-hidden="true"
                        />
                      )}
                      <span>{opt.label}</span>
                    </button>
                  );
                })}
              </div>
              <p className={sectionStyles.modeTabDescription}>{MODE_OPTIONS.find((o) => o.value === mode)?.desc}</p>
            </div>

            {/* 選んだ決め方の設定 */}
            <div className={styles.settings}>
              {mode === 'generate' && (
                <>
                  <div className={styles.field}>
                    <span className={styles.fieldLabel}>作曲AIモデル</span>
                    <SegmentedTabs
                      className={styles.providerTabs}
                      tone="neutral"
                      ariaLabel="作曲AIモデル"
                      options={PROVIDER_OPTIONS}
                      value={provider}
                      onChange={setProvider}
                    />
                    <span className={styles.hint}>BGMの長さ: 約{bgmSeconds}秒</span>
                  </div>
                  {/* プロンプトの方式・ボーカル・曲の詳細はシナリオ編集画面の BGM セクションと同じ部品と並び。
                      テンプレートのときはインスト固定のためボーカルと詳細を出さない (通常画面と同じ) */}
                  {bgmTemplateOptions.length > 0 && (
                    <div className={styles.field}>
                      <span className={styles.fieldLabel}>プロンプト</span>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', alignItems: 'flex-start' }}>
                        <ProviderSegmentedControl
                          options={[
                            { value: 'standard' as const, label: '標準', tooltip: 'モーショングラフィックス向けに自動で組んだプロンプトで作曲' },
                            { value: 'template' as const, label: 'テンプレート', tooltip: '用意された曲調テンプレートから選んで作曲' },
                          ]}
                          value={promptMode}
                          onChange={setPromptMode}
                        />
                        {templateMode && (
                          <div style={{ display: 'inline-flex' }}>
                            <SingleSelectDropdown
                              label="BGMテンプレート"
                              labelOnly
                              alwaysShowSelected
                              fitContent
                              value={effectiveTemplateKey ?? ''}
                              onChange={(v) => setTemplateKey(v)}
                              options={bgmTemplateOptions}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  {!templateMode && (
                    <>
                      <div className={styles.field}>
                        <label className={styles.checkRow}>
                          <input type="checkbox" checked={vocal} onChange={(e) => setVocal(e.target.checked)} />
                          <span>ボーカルを含める</span>
                        </label>
                      </div>
                      <div className={styles.field}>
                        <label className={styles.checkRow}>
                          <input type="checkbox" checked={detailOpen} onChange={(e) => setDetailOpen(e.target.checked)} />
                          <span>曲の詳細を設定する</span>
                        </label>
                        {detailOpen && (
                          <div className={sectionStyles.optionFormBody}>
                            <BgmSongDetailFields
                              value={detail}
                              onChange={(patch) => setDetail((prev) => ({ ...prev, ...patch }))}
                              bpmMin={MOTION_BGM_BPM_MIN}
                              bpmMax={MOTION_BGM_BPM_MAX}
                            />
                          </div>
                        )}
                      </div>
                    </>
                  )}
                  <div className={styles.field}>
                    <label className={styles.checkRow}>
                      <input type="checkbox" checked={autoContinue} onChange={(e) => setAutoContinue(e.target.checked)} />
                      <span>BGMの生成が完了したら、試聴せずにシナリオ生成まで行う</span>
                    </label>
                  </div>
                </>
              )}

              {mode === 'material' && (
                <div className={pickerStyles.panel}>
                  <div className={pickerStyles.panelLead}>BGMに設定する楽曲を選んでください</div>
                  {renderCurrentRow(pickedMaterial, 'まだ選ばれていません', ['素材を選ぶ', '別の素材を選ぶ'], () => setMaterialPickerOpen(true))}
                </div>
              )}

              {mode === 'free' && (
                <div className={pickerStyles.panel}>
                  <div className={pickerStyles.panelLead}>用意された楽曲からBGMを選んでください</div>
                  {renderCurrentRow(
                    pickedFreeTrack ? { name: pickedFreeTrack.name, durationSeconds: pickedFreeTrack.duration_seconds, url: pickedFreeTrack.preview_url } : null,
                    'まだ選ばれていません',
                    ['楽曲を選ぶ', '別の楽曲を選ぶ'],
                    () => setFreePickerOpen(true),
                  )}
                </div>
              )}

              {mode === 'upload' && (
                <div className={pickerStyles.panel}>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept={BGM_UPLOAD_ACCEPT}
                    style={{ display: 'none' }}
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      e.target.value = '';
                      void handleFile(file);
                    }}
                  />
                  {/* アップロード済みの曲は素材 / フリー楽曲タブと同じ「選択中」行 (試聴ボタン付き)。
                      「別のファイルをアップロード」でドロップ領域を再表示する */}
                  {uploaded && renderCurrentRow(uploaded, 'まだ選ばれていません', ['ファイルをアップロード', '別のファイルをアップロード'], () => setUploadZoneOpen((v) => !v))}
                  {busy ? (
                    <span className={styles.hint}>アップロード中…</span>
                  ) : (!uploaded || uploadZoneOpen) && (
                    // シナリオ編集画面のBGMアップロードと同じドロップゾーン。
                    // .bgmDropZone の min-height:100% が flex 子要素だと親の高さを巡回参照して見出し分はみ出すため、
                    // ブロックの div で一段包む
                    <div>
                      <BgmUploadDropZone onFile={(file) => { void handleFile(file); }} onChooseFile={() => fileInputRef.current?.click()} />
                    </div>
                  )}
                </div>
              )}

              {mode !== 'none' && (
                // 「BGMの拍に合わせる」(§10.9)。台本を小節で書くか秒で書くかが変わるので生成前にここで決める (生成後は作り直し)。
                // BGM の決め方とは別の設定なので区切り線で分ける
                <div className={`${styles.field} ${styles.sectionDivider}`} role="radiogroup" aria-label="BGMの拍に合わせるか">
                  <label className={styles.checkRow}>
                    <input type="radio" name="motion-bgm-beat-sync" checked={beatSync} onChange={() => setBeatSync(true)} data-testid="motion-bgm-beat-sync" />
                    <span>BGMの拍に合わせる</span>
                    <span className={styles.hint}>小節や拍に合わせてシーンを切り替えます</span>
                  </label>
                  <label className={styles.checkRow}>
                    <input type="radio" name="motion-bgm-beat-sync" checked={!beatSync} onChange={() => setBeatSync(false)} data-testid="motion-bgm-beat-sync-off" />
                    <span>BGMの拍に合わせない</span>
                    <span className={styles.hint}>シーンの長さはナレーションや台本に連動します</span>
                  </label>
                </div>
              )}

            </div>
          </div>

          <div className={styles.footer}>
            <button type="button" className="btn-modal btn-modal-secondary" onClick={onCancel} disabled={busy}>
              キャンセル
            </button>
            <div className={styles.footerActions}>
              {costLine && <span className={styles.footerCost}>{costLine}</span>}
              {missing && !busy && (
                <span className={styles.validationWarning}>{missing}</span>
              )}
              <GenerateButton
                label="シナリオを生成"
                creditCost={credits ? formatCreditRange(credits.total) : undefined}
                onClick={() => { void handleConfirm(); }}
                disabled={!canConfirm}
                isLoading={busy}
                loadingLabel={mode === 'upload' ? 'アップロード中…' : '処理中…'}
              />
            </div>
          </div>
        </div>
      </div>

      {/* 素材から選ぶ: 動画エディタのBGMと同じ音声素材ピッカー */}
      {materialPickerOpen && (
        <MaterialsPickerModal
          title="BGMにする音声素材を選択"
          fileTypes={PICKER_FILE_TYPES}
          showSourceTypeFilter
          initialSourceTypes={PICKER_INITIAL_SOURCE_TYPES}
          hideAspectRatioFilter
          hideFileSizeBadge
          onSelect={(_url, materialId) => { if (materialId) void handlePickMaterial(materialId); }}
          onClose={() => setMaterialPickerOpen(false)}
        />
      )}

      {/* フリー楽曲: BGM セクションと同じカタログ一覧。ここでは選ぶだけで、複製は確定時に行う */}
      {freePickerOpen && (
        <BgmFreeTrackPickerModal
          selectedCatalogId={pickedFreeTrack?.id ?? null}
          busyCatalogId={null}
          onPick={(item) => { setPickedFreeTrack(item); return true; }}
          onClose={() => setFreePickerOpen(false)}
        />
      )}
    </>,
    document.body,
  );
}
